import React, { useState, useRef, useLayoutEffect } from "react";

/**
 * Image that fades in once loaded, showing a dark-purple gradient placeholder
 * meanwhile — avoids the black "flash" while cover images decode. Drop-in for
 * an <img> that fills a positioned parent (pass the same className, e.g.
 * "absolute inset-0 h-full w-full object-cover").
 *
 * Cached images (already decoded, e.g. when re-entering the page) skip the fade
 * entirely, so navigating back doesn't look like everything reloads.
 */
export default function FadeImg({ src, className = "", alt = "", ...props }) {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // If the browser already has the image (cache hit), the <img> is `complete`
  // on mount and onLoad may never fire — mark it loaded before paint (layout
  // effect) so re-entering the page shows it instantly, with no fade or flash.
  useLayoutEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  return (
    <>
      {!loaded && (
        <div
          aria-hidden
          className={`${className} animate-pulse bg-gradient-to-br from-[#1b1030] to-[#0b0510]`}
        />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="eager"
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={`${className} transition-[opacity,transform] duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
        {...props}
      />
    </>
  );
}
